import {
  useCallback,
  useEffect,
  useReducer,
  useState,
  useRef,
  useMemo,
  useContext,
} from "react";

import PropTypes from "prop-types";

import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { useForm } from "react-hook-form";

import { formReducer, types } from "./shared/formReducer";
import { useDefaults } from "./shared/useDefaults";
import { useFunctionRecompute } from "./shared/useFunctionRecompute";
import { useFetchForm } from "~/hooks/api/form/useFetchForm";
import { LoadingContext, actionTypes } from "~/context/LoadingContext";
import { PreviewModeContext, RenderMode } from "~/context/PreviewModeContext";

import { Box } from "~/components/Base/Box/Box";
import { FormContent } from "./FormContent/FormContent";
import { FormHeader } from "./FormHeader/FormHeader";
import { FormNavigation } from "./FormNavigation/FormNavigation";
import { FormSubmitPage } from "./FormSubmitPage/FormSubmitPage";
import { FORM_LAYOUT } from "./shared/form-layouts";
import { getDeviceInformation, isReadOnly } from "./shared/form-helpers";
import { scrollToItem } from "./shared/scroll-to-item";

import "./Form.css";
import { useSendForm } from "~/hooks/api/form/useSendForm";

export const FORM_PAGE = Object.freeze({
  SUBMIT: "submit",
});

// Defined in shared/form-layouts (a leaf module, so tests can import the
// real values while mocking this module); re-exported to keep the public
// import path.
export { FORM_LAYOUT };

/**
 * Presentational form renderer. Owns RHF state, paging math, page rendering
 * and submit orchestration. Holds no router or data-fetching hooks — the
 * controller wires those via `onSubmit`, `onNavigate`, `isLoading`, `error`,
 * `isSending`.
 *
 * Mode-agnostic: it knows nothing about preview/print. Capabilities are
 * expressed as optional props, and the composition root decides; this
 * component never branches on render mode.
 *   - `onSubmit` absent (as in preview/print) → Submit is disabled and
 *     `submitForm` is a no-op.
 *   - `eventId` absent (as in preview/print, which have no /events route) →
 *     the submit page's unanswered-question entries jump in-app via
 *     `onNavigate` instead of rendering a deep link.
 *   - `onComputedTypeMismatch` present (as in preview) → Computation results
 *     the item cannot render are reported to the composition root so it can
 *     show them. The live flow passes none: the value is cleared and logged
 *     either way, but a patient is never shown designer-facing text.
 */
export function FormView({
  page,
  form,
  dispatch,
  onSubmit,
  onNavigate,
  eventId,
  onComputedTypeMismatch,
  isLoading = false,
  isSending = false,
  error = null,
  layout = FORM_LAYOUT.PAGED,
}) {
  const isAllGroups = layout === FORM_LAYOUT.ALL_GROUPS;

  const [searchParams] = useSearchParams();

  const formHeader = useRef(null);
  const validateOnLoad = useRef(false);
  const isReadyToSubmit = useRef(false);
  // Item to validate-and-reveal once the target page has rendered, set when the
  // submit page navigates in-app instead of through a deep link. See
  // `goToUnansweredQuestion`.
  const pendingItemReveal = useRef(null);

  const [submitFailed, setSubmitFailed] = useState();
  const [submitErrorMessage, setSubmitErrorMessage] = useState();
  const { dispatch: dispatchLoading } = useContext(LoadingContext);
  const [showFormNavigation, setShowFormNavigation] = useState(false);
  const [previousPage, setPreviousPage] = useState();

  const {
    control,
    formState: { errors, dirtyFields, touchedFields },
    trigger,
    clearErrors,
    handleSubmit,
    resetField,
    setValue,
    setError,
    // criteriaMode "all" keeps every failing rule in `errors[oid].types` so an
    // item with several Hard checks can show all of its violated messages at
    // once instead of only the first.
  } = useForm({ delayError: 500, mode: "onChange", criteriaMode: "all" });

  // Fields eligible for cross-field re-validation: user-touched or currently
  // errored. Refreshed every render so the microtask in setItemValue reads
  // current state.
  const revalidateFieldsRef = useRef([]);
  revalidateFieldsRef.current = [
    ...new Set([
      ...Object.keys(errors),
      ...Object.keys(dirtyFields),
      ...Object.keys(touchedFields),
    ]),
  ];

  const setItemValue = useCallback(
    (id, value, format) => {
      dispatch({
        type: types.SET_ITEM_VALUE,
        payload: { id, value, format, resetField },
      });
      // A write to one field won't re-run RHF validators for other fields
      // whose cross-field rangeChecks depend on it, so re-validate those after
      // the dispatch lands — but only dirty/touched/errored fields, so
      // programmatic writes on a fresh form don't surface errors on untouched
      // fields before page submit. Intentional: a cross-field error
      // on a field the user never touched appears at page submit, not while
      // typing in the field it depends on.
      Promise.resolve().then(() => {
        const fields = revalidateFieldsRef.current;
        if (fields.length) {
          trigger(fields);
        }
      });
    },
    [dispatch, resetField, trigger]
  );

  // Items that clear through their own reset control (VasScale, DrawingPad)
  // pass the empty value they want stored. It has to be given to resetField
  // explicitly: without a defaultValue it writes `undefined`, which RHF reads
  // back as the field's mount-time default, so an item reset after its item
  const resetItemValue = useCallback(
    (id, emptyValue) => resetField(id, { defaultValue: emptyValue }),
    [resetField]
  );

  const readOnly = useMemo(() => isReadOnly(form?.status), [form]);

  // Both hooks report through the same callback. Their OIDs cannot collide: an
  // item carries one methodDef, so it is either an Imputation default or a
  // Computation, never both.
  useDefaults({
    form,
    setValue,
    setItemValue,
    readOnly,
    onTypeMismatch: onComputedTypeMismatch,
  });
  useFunctionRecompute({
    form,
    setValue,
    setItemValue,
    readOnly,
    onTypeMismatch: onComputedTypeMismatch,
  });

  // All-groups guards: the submit page and the out-of-range redirect are
  // paging concepts — a stale `page` value (e.g. "submit" at the moment of a
  // layout toggle) must neither swap the content nor bounce to page 1.
  const isSubmitPage = !isAllGroups && page === FORM_PAGE.SUBMIT;
  const visibleItemGroups = form.itemGroups.filter((ig) => ig.isVisible);
  const pageCount = visibleItemGroups.length;
  const currentPage = isSubmitPage ? pageCount + 1 : Number.parseInt(page);
  const displayPageCount = readOnly ? pageCount : pageCount + 1; // includes submit page when not readonly
  const isPageOutOfRange =
    !isAllGroups && (currentPage > displayPageCount || currentPage < 1);
  const currentItemGroup = visibleItemGroups[currentPage - 1];

  const submitForm = async () => {
    // No save path wired (preview/print pass no onSubmit) — nothing to send.
    // The Submit button is already disabled in that case; this guards the
    // handler too against a programmatic submit reaching an absent onSubmit.
    if (!onSubmit) {
      return;
    }

    const LOADING_KEY = "submit-form";
    dispatchLoading({
      type: actionTypes.ADD_LOADING_ITEM,
      key: LOADING_KEY,
    });

    try {
      const result = await onSubmit({
        formData: form.formData,
        deviceInformation: getDeviceInformation(),
      });

      if (result.success) {
        onNavigate("home", {
          replace: true,
          state: {
            message: "form.thank_you.submit_success",
            preventBackOnBrowser: true,
          },
        });
        return;
      }

      // #101278 — map server-side hard violations to RHF errors and navigate
      // to the page containing the first failing item.
      if (result.hardViolations?.length) {
        for (const v of result.hardViolations) {
          setError(v.itemOID, { type: "server", message: v.errorMessage });
        }

        const firstOID = result.hardViolations[0].itemOID;
        const targetPage = visibleItemGroups.findIndex((ig) =>
          ig.items?.some((item) => item.oid === firstOID)
        );

        if (targetPage !== -1) {
          // targetPage is 0-based; pages are 1-based
          onNavigate("page", targetPage + 1);

          // Wait for the page render, then scroll the failing item into view.
          setTimeout(() => {
            scrollToItem(firstOID);
          }, 0);
        }

        setSubmitErrorMessage("Some fields need correction");
        // Do NOT set submitFailed — the inline RHF errors serve as the
        // feedback.  The summary message above gives a header hint.
        return;
      }

      // Generic server error (no structured body)
      setSubmitErrorMessage(result.message);
      setSubmitFailed(true);
    } catch (e) {
      setSubmitFailed(true);
    } finally {
      dispatchLoading({
        type: actionTypes.REMOVE_LOADING_ITEM,
        key: LOADING_KEY,
      });
    }
  };

  useEffect(() => {
    const LOADING_KEY = "form";
    const type = isLoading
      ? actionTypes.ADD_LOADING_ITEM
      : actionTypes.REMOVE_LOADING_ITEM;

    dispatchLoading({
      type,
      key: LOADING_KEY,
    });
  }, [dispatchLoading, isLoading]);

  // Runs when the item group changes, i.e. after landing on a page. Handles
  // both ways of arriving from an unanswered-question link: a deep link
  // carrying `?validate=true#oid` (live), and an in-app jump that parked the
  // oid in `pendingItemReveal` (preview). Either way validation covers only
  // the page just landed on — `onNavigate("page", n)` drops the query string,
  // so live doesn't carry `validate` onward either.
  // biome-ignore lint/correctness/useExhaustiveDependencies: This triggers a validation when item group changes. Could be good to look into if there is a better way of achieving this.
  useEffect(() => {
    const revealOid = pendingItemReveal.current;
    pendingItemReveal.current = null;

    validateOnLoad.current = Boolean(revealOid) || searchParams.get("validate") === "true";

    if (validateOnLoad.current) {
      trigger();
    }

    if (revealOid) {
      scrollToItem(revealOid);
    }
  }, [currentItemGroup]);

  useEffect(() => {
    if (!submitFailed) {
      setSubmitErrorMessage();
    }
  }, [submitFailed]);

  // If the requested page is out of range — e.g. a dynamic visibility
  // recompute shrank the visible page count below the current page, or a
  // stale/bookmarked URL points past the end — bounce to page 1. The base
  // FormInner did this with a render-phase `<Navigate to=.../form/1>`; the
  // presentational FormView instead delegates to the controller via
  // `onNavigate` so it stays router-agnostic. The out-of-range guard below
  // renders nothing while this effect redirects.
  useEffect(() => {
    if (pageCount && isPageOutOfRange) {
      onNavigate("page", 1);
    }
  }, [pageCount, isPageOutOfRange, onNavigate]);

  // Reset the show form navigation when navigating to a new page
  // the form navigation will be hidden until the form content animation is done
  if (page !== previousPage) {
    setPreviousPage(page);
    setShowFormNavigation(false);
  }

  const getNavigation = () => {
    return (
      <FormNavigation
        canSubmit={Boolean(onSubmit)}
        currentPage={currentPage}
        readOnly={readOnly || isSending}
        pageCount={displayPageCount}
        isSubmitPage={isSubmitPage}
        onClickPrevious={(toPage) => {
          if (toPage < 1) {
            onNavigate("events");
          } else {
            clearErrors();
            setSubmitFailed(false);
            onNavigate("page", toPage);
          }
        }}
        onClickNext={(page) => {
          handleSubmit(
            () => {
              page > pageCount
                ? onNavigate("submit")
                : onNavigate("page", page);
            },
            (validationErrors) => {
              // Scroll the first invalid item (in visual order) into view.
              // The errors object's key order is not the visual order, so
              // walk the current page's items instead.
              const firstInvalid = currentItemGroup?.items?.find(
                (item) => validationErrors[item.oid]
              );
              if (firstInvalid) {
                scrollToItem(firstInvalid.oid);
              }
            }
          )();
        }}
        onSubmit={() => {
          if (isReadyToSubmit.current) {
            submitForm();
          }
        }}
      />
    );
  };

  // In-app jump to an unanswered question. Always available, because paging is
  // always available; the deep-link variant on the submit page is the
  // progressive enhancement layered on top when `eventId` gives it a URL to
  // point at. The reveal itself is deferred to the effect above, which fires
  // once the target page has rendered.
  const goToUnansweredQuestion = (toPage, oid) => {
    pendingItemReveal.current = oid;
    onNavigate("page", toPage);
  };

  const getFormContent = () => {
    if (isSubmitPage) {
      return (
        <FormSubmitPage
          submitFailed={submitFailed}
          submitErrorMessage={submitErrorMessage}
          itemGroups={visibleItemGroups}
          formData={form.formData}
          eventId={eventId}
          onGoToQuestion={goToUnansweredQuestion}
          onAnimationEnd={() => {
            setShowFormNavigation(true);
          }}
          onSubmitValidation={(valid) => {
            isReadyToSubmit.current = valid;
          }}
        />
      );
    }

    return (
      <FormContent
        control={control}
        errors={errors}
        formData={form.formData}
        itemGroups={isAllGroups ? visibleItemGroups : [currentItemGroup]}
        readOnly={readOnly || isSending}
        onAnimationEnd={() => {
          setShowFormNavigation(true);
        }}
        onChange={setItemValue}
        onReset={resetItemValue}
        formHeaderRef={formHeader}
      />
    );
  };

  if (isLoading) {
    return null;
  }

  // Render nothing while the out-of-range redirect effect above runs.
  if (pageCount && isPageOutOfRange) {
    return null;
  }

  return (
    <Box
      footer={isAllGroups ? null : getNavigation()}
      showFooter={showFormNavigation}
    >
      <FormHeader
        currentPage={currentPage ? currentPage : pageCount}
        formName={form.formName}
        isPending={isLoading}
        isReadyToSubmit={isReadyToSubmit.current}
        pageCount={displayPageCount}
        ref={formHeader}
        showFetchError={!!error}
        showProgress={!isAllGroups}
        targetDate={form?.targetDate}
        valid={Object.keys(errors).length === 0}
      />
      {form && getFormContent()}
    </Box>
  );
}

FormView.propTypes = {
  page: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onSubmit: PropTypes.func, // optional — absent in preview/print (no save path)
  onNavigate: PropTypes.func.isRequired,
  eventId: PropTypes.string, // optional — only the live flow has a /events route to deep-link into
  onComputedTypeMismatch: PropTypes.func, // optional — preview only; see above
  isLoading: PropTypes.bool,
  isSending: PropTypes.bool,
  error: PropTypes.any,
  layout: PropTypes.oneOf(Object.values(FORM_LAYOUT)), // optional — preview passes "all-groups"; live never passes it
};

/**
 * Live patient-flow controller. Wraps <FormView> with the SWR fetch, the
 * submit POST, and the router-driven navigation.
 */
function FormController({ page, form, dispatch }) {
  const navigate = useNavigate();
  const { eventId } = useParams();

  const { sendForm, isSending } = useSendForm(eventId);

  const formContextRef = useRef({ formData: {}, dependencies: {}, identifierModel: {} });

  const { error, isLoading } = useFetchForm(eventId, (data) =>
    dispatch({
      type: types.SET_FORM,
      payload: data,
      contextRef: formContextRef,
    })
  );

  formContextRef.current = {
    formData: form.formData ?? {},
    dependencies: form.dependencies ?? {},
    identifierModel: form.identifierModel ?? {},
  };

  const onSubmit = useCallback(
    async ({ formData, deviceInformation }) => {
      return sendForm({ formData, ...deviceInformation });
    },
    [sendForm]
  );

  const onNavigate = useCallback(
    (target, arg) => {
      switch (target) {
        case "page":
          navigate(`/events/${eventId}/form/${arg}`);
          return;
        case "submit":
          navigate(`/events/${eventId}/form/submit`);
          return;
        case "thank-you":
          navigate(`/events/${eventId}/form/thank-you`, arg ?? { replace: true });
          return;
        case "home":
          navigate("/", arg);
          return;
        case "events":
          navigate("/events");
          return;
        default:
          return;
      }
    },
    [navigate, eventId]
  );

  return (
    <FormView
      page={page}
      form={form}
      dispatch={dispatch}
      onSubmit={onSubmit}
      onNavigate={onNavigate}
      eventId={eventId}
      isLoading={isLoading}
      isSending={isSending}
      error={error}
    />
  );
}

FormController.propTypes = {
  page: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

// Stable PreviewModeContext value for the live patient flow — kept module-
// scoped so the Provider doesn't re-render its subtree on every <Form>
// render.
const LIVE_PREVIEW_MODE = Object.freeze({ mode: RenderMode.LIVE });

export function Form({ page }) {
  const [{ form }, dispatch] = useReducer(formReducer, {
    form: { itemGroups: [] },
  });

  // Wrap in PreviewModeContext.Provider with mode 'live' for symmetry with
  // <PreviewForm>'s mode 'preview'. file-touching hooks default to 'live'
  // when no provider is in scope, so this is currently a no-op for
  // behaviour; it just makes the context explicit for the live tree.
  // See docs/features/2-preview-route.md.
  return (
    <PreviewModeContext.Provider value={LIVE_PREVIEW_MODE}>
      <FormController page={page} form={form} dispatch={dispatch} />
    </PreviewModeContext.Provider>
  );
}

Form.propTypes = {
  page: PropTypes.string.isRequired,
};
